import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { statuteReducer } from "../../../state/reducers";
import { IStatute } from "../../../utils/types/models";

const StatuteEditor: React.FC<IAddEntityScreenProps<IStatute> | IEditEntityScreenProps<IStatute>> = (props: IAddEntityScreenProps<IStatute> | IEditEntityScreenProps<IStatute>) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(statuteReducer, getInitialState<IStatute>());
	const initialStatute: IStatute = {
		id: (props as IEditEntityScreenProps<IStatute>).recordId || 0,
		name: "",
		active: true
	};
	const [statute, setStatute] = useState<IStatute>(initialStatute);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setStatute(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent): void {
		setStatute(handleChange(statute, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(statute.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: IStatute): void {
		if (record && !close) {
			setStatute(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<IStatute>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={statute}
			endpoint={Endpoint.Statutes}
			entityState={state}
			entityType={t("statute")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={statute.name}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} value={statute.name} onChange={onChange} required disabled={readonly} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={statute.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default StatuteEditor;
